/**
 * Offline write queue backed by IndexedDB (native browser API, no deps).
 *
 * When the device is offline, the mutation helpers below stash the request in
 * IndexedDB and dispatch the `offline-queue-changed` event. When the browser
 * regains connectivity (`window.online`), `replayQueue()` flushes pending
 * items against the live API in FIFO order.
 *
 * Only mutations that make sense to replay (writes that don't expect a
 * server-generated response the UI is waiting on) should be wrapped.
 * Read-only GETs should stay online-only.
 *
 * Items carry a `status` of `'pending'` (default) or `'quarantined'`.
 * Transient failures (network drop, 401, 408, 429, 5xx) break the replay
 * and leave the item pending for the next online/sync event. Non-retryable
 * 4xx responses (bad payload, permission denied, conflict) flip the item
 * to `'quarantined'` so it's preserved for review rather than silently
 * deleted. See `listQuarantined`, `requeueItem`, `discardItem`.
 */

const DB_NAME = 'wildebeef-offline'
const STORE = 'pending-writes'
const EVENT_NAME = 'offline-queue-changed'
const FAILURE_EVENT_NAME = 'offline-queue-failure'

let dbPromise = null

/** Per-write idempotency key (UUID). Falls back to a random string if the
 *  Web Crypto UUID API is unavailable; the server then just skips the guard
 *  for that write (graceful degrade — a non-secure context shouldn't occur in
 *  the HTTPS deployment). */
function newIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'idk-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2)
}

function openDB() {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
  return dbPromise
}

function tx(mode = 'readonly') {
  return openDB().then((db) => db.transaction(STORE, mode).objectStore(STORE))
}

function notifyChange() {
  window.dispatchEvent(new CustomEvent(EVENT_NAME))
}

function dispatchFailure(item, classification) {
  window.dispatchEvent(new CustomEvent(FAILURE_EVENT_NAME, {
    detail: { item, classification },
  }))
}

function isPending(row) {
  return row.status !== 'quarantined'
}

export async function enqueue({ method, url, data, label, idempotencyKey }) {
  const store = await tx('readwrite')
  return new Promise((resolve, reject) => {
    const req = store.add({
      method,
      url,
      data,
      label: label || `${method} ${url}`,
      // Stable per-write key so a replay (after a lost response) is deduped
      // server-side instead of creating a duplicate row. See app/idempotency.py.
      idempotency_key: idempotencyKey || newIdempotencyKey(),
      queued_at: new Date().toISOString(),
      status: 'pending',
    })
    req.onsuccess = () => {
      notifyChange()
      resolve(req.result)
    }
    req.onerror = () => reject(req.error)
  })
}

async function getAll() {
  const store = await tx('readonly')
  return new Promise((resolve, reject) => {
    const req = store.getAll()
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

export async function listPending() {
  return (await getAll()).filter(isPending)
}

export async function listQuarantined() {
  return (await getAll()).filter((row) => row.status === 'quarantined')
}

export async function pendingCount() {
  return (await listPending()).length
}

export async function quarantinedCount() {
  return (await listQuarantined()).length
}

async function remove(id) {
  const store = await tx('readwrite')
  return new Promise((resolve, reject) => {
    const req = store.delete(id)
    req.onsuccess = () => {
      notifyChange()
      resolve()
    }
    req.onerror = () => reject(req.error)
  })
}

async function updateRow(id, mutator) {
  const store = await tx('readwrite')
  return new Promise((resolve, reject) => {
    const getReq = store.get(id)
    getReq.onsuccess = () => {
      const row = getReq.result
      if (!row) return resolve(null)
      const next = mutator(row)
      const putReq = store.put(next)
      putReq.onsuccess = () => {
        notifyChange()
        resolve(next)
      }
      putReq.onerror = () => reject(putReq.error)
    }
    getReq.onerror = () => reject(getReq.error)
  })
}

/**
 * Pure classifier: maps an axios replay error to one of two actions.
 * Exported so it can be unit-tested once a frontend test harness exists.
 *
 *   { action: 'break', reason: 'network' | 'auth' | 'timeout' | 'rate-limited' | 'server', retryAfter? }
 *   { action: 'quarantine', reason: 'client-error', status, message }
 *
 * `break` means stop the flush — the item stays pending for the next
 * online/sync event. `quarantine` means flip this one item to quarantined
 * and keep replaying the rest of the queue.
 */
export function classifyReplayError(err) {
  if (!err?.response) {
    return { action: 'break', reason: 'network' }
  }
  const status = err.response.status
  if (status === 401) return { action: 'break', reason: 'auth', status }
  if (status === 408) return { action: 'break', reason: 'timeout', status }
  // 425 Too Early = the idempotency middleware has the same key in flight.
  // Transient: keep the item pending and retry on the next sync (do NOT
  // quarantine — unlike a genuine 409 conflict).
  if (status === 425) return { action: 'break', reason: 'in-flight', status }
  if (status === 429) {
    const header = err.response.headers?.['retry-after']
    const retryAfter = header ? Number(header) : null
    return {
      action: 'break',
      reason: 'rate-limited',
      status,
      retryAfter: Number.isFinite(retryAfter) ? retryAfter : null,
    }
  }
  if (status >= 500) return { action: 'break', reason: 'server', status }
  // 4xx (except the ones above): bad payload / permission / conflict — not
  // going to succeed on retry. Quarantine so the write isn't silently lost.
  const message =
    err.response.data?.detail ||
    err.response.data?.message ||
    err.message ||
    `HTTP ${status}`
  return { action: 'quarantine', reason: 'client-error', status, message }
}

let replayInFlight = false

/**
 * Replay pending writes against the live API, FIFO.
 *
 *   - Transient failure (network / 401 / 408 / 429 / 5xx): stop the flush,
 *     item stays pending. The next `online` event or manual Sync click
 *     will try again.
 *   - Client 4xx (bad payload, 403, 404, 409, 422…): flip the item to
 *     quarantined and keep replaying the rest. Quarantined items never
 *     auto-retry — surface them via `listQuarantined` and clear via
 *     `requeueItem` or `discardItem`.
 */
export async function replayQueue(api) {
  if (replayInFlight) return { replayed: 0, quarantined: 0 }
  if (!navigator.onLine) return { replayed: 0, quarantined: 0 }
  replayInFlight = true

  let replayed = 0
  let quarantined = 0
  try {
    const pending = await listPending()
    pending.sort((a, b) => a.id - b.id)

    for (const item of pending) {
      try {
        await api.request({
          method: item.method,
          url: item.url,
          data: item.data,
          // Flags the request as part of a backlog flush so the API
          // gateway's global rate-limit default (100/min/user) doesn't
          // 429 legitimate sync traffic. Per-endpoint limits still apply.
          // Idempotency-Key dedupes a replay whose first response was lost.
          headers: {
            'X-Offline-Sync': '1',
            ...(item.idempotency_key ? { 'Idempotency-Key': item.idempotency_key } : {}),
          },
        })
        await remove(item.id)
        replayed += 1
      } catch (err) {
        const classification = classifyReplayError(err)
        if (classification.action === 'quarantine') {
          await updateRow(item.id, (row) => ({
            ...row,
            status: 'quarantined',
            failure: {
              status: classification.status ?? null,
              reason: classification.reason,
              message: classification.message ?? null,
              at: new Date().toISOString(),
            },
          }))
          dispatchFailure(item, classification)
          quarantined += 1
          continue
        }
        break
      }
    }
  } finally {
    replayInFlight = false
  }
  return { replayed, quarantined }
}

/**
 * Wrap an axios POST/PUT/PATCH so that, when offline, it enqueues and
 * resolves with a stub "queued" response instead of throwing.
 */
export async function queuedMutation(api, { method, url, data, label }) {
  // One stable key reused across the online attempt AND any later replay, so a
  // write the server processed but whose response was lost can be re-sent safely.
  const idempotencyKey = newIdempotencyKey()
  if (navigator.onLine) {
    try {
      const res = await api.request({
        method,
        url,
        data,
        headers: { 'Idempotency-Key': idempotencyKey },
      })
      return res.data
    } catch (err) {
      // A real HTTP error (4xx/5xx) surfaces to the caller exactly as before.
      // Only a network-class failure (no response) falls through to the queue —
      // re-sending under the same key is safe because the server dedupes on it.
      if (err && err.response) throw err
    }
  }
  const id = await enqueue({ method, url, data, label, idempotencyKey })
  return { _offline_queued: true, _queue_id: id, label: label || `${method} ${url}` }
}

/** Flip a quarantined item back to pending so the next replay picks it up. */
export async function requeueItem(id) {
  await updateRow(id, (row) => {
    const { failure: _failure, ...rest } = row
    return { ...rest, status: 'pending' }
  })
}

/** Permanently delete an item (pending or quarantined). */
export async function discardItem(id) {
  await remove(id)
}

export function subscribe(listener) {
  window.addEventListener(EVENT_NAME, listener)
  window.addEventListener('online', listener)
  window.addEventListener('offline', listener)
  return () => {
    window.removeEventListener(EVENT_NAME, listener)
    window.removeEventListener('online', listener)
    window.removeEventListener('offline', listener)
  }
}

/** Subscribe specifically to quarantine events (for toasts, telemetry, etc.). */
export function subscribeFailures(listener) {
  window.addEventListener(FAILURE_EVENT_NAME, listener)
  return () => window.removeEventListener(FAILURE_EVENT_NAME, listener)
}

/**
 * Hook the replay mechanism to the `online` event. Call once at app start.
 */
export function installReplayListener(api) {
  const handler = () => { replayQueue(api).catch(() => {}) }
  window.addEventListener('online', handler)
  // Also try once at startup in case we reconnected before JS loaded
  if (navigator.onLine) handler()
}
